'use client';

import { getClientLogger } from '@/lib/logger/pino-logger.client';
import { convertFiltersBase } from '@lamesarv-sdk/tools';
import {
  IBasicInventory,
  IGetFacetItems,
  IGetInventoryItem,
  IGetInventoryItems,
  IInventory,
  InventoryFiltersBase,
  IRawFacets,
  ISearchResults,
} from '@lamesarv-sdk/types';
import { useEffect, useState } from 'react';

export const useGetFilters = (props: IGetFacetItems) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [items, setItems] = useState<InventoryFiltersBase>({} as InventoryFiltersBase);

  const logger = getClientLogger('hooks.inventory.useGetFilters');

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError(false);

      const response = await fetch('/api/inventory', {
        method: 'POST',
        body: JSON.stringify({
          action: 'getFilters',
          payload: {
            filters: props.filters,
          },
        }),
      });
      const rawData: IRawFacets = await response.json();

      const data = convertFiltersBase(rawData);

      setItems(data);

      setLoading(false);

      return data;
    } catch (error) {
      logger.error(`Error fetching filters: ${(error as Error).message}`);

      setLoading(false);
      setError(true);

      return {};
    }
  };

  useEffect(() => {
    if (!props?.skipFetch) {
      fetchItems();
    }
  }, []);

  return {
    loading,
    error,
    items,
    fetchItems,
  };
};

export const useGetInventoryItems = (props: IGetInventoryItems) => {
  const [loading, setLoading] = useState<boolean>(props.startLoading || false);
  const [error, setError] = useState<boolean>(false);
  const [items, setItems] = useState<ISearchResults<IBasicInventory>>({
    hits: [],
    nbHits: 0,
    nbPages: 0,
  });

  const resetItems = () => {
    setItems({
      hits: [],
      nbHits: 0,
      nbPages: 0,
    });
    setLoading(false);
    setError(false);
  };

  const logger = getClientLogger('hooks.inventory.useGetInventoryItems');

  const fetchItems = async (fetchProps?: IGetInventoryItems): Promise<ISearchResults<IBasicInventory>> => {
    const _props = fetchProps ?? props;

    try {
      setLoading(true);
      setError(false);

      const response = await fetch('/api/inventory', {
        method: 'POST',
        body: JSON.stringify({
          action: 'getItems',
          payload: _props,
        }),
      });
      const data: ISearchResults<IBasicInventory> = await response.json();

      const { hits, found } = data;
      setItems({
        hits,
        nbHits: found ?? 0,
        nbPages: Math.ceil((found ?? 0) / (_props?.perPage ?? 1)),
      });

      setLoading(false);

      return data;
    } catch (error) {
      logger.error(`Error fetching inventory items: ${(error as Error).message}`);

      setLoading(false);
      setError(true);
      return {
        hits: [],
        nbHits: 0,
        nbPages: 0,
      };
    }
  };

  useEffect(() => {
    if (!props.skipFetch) {
      fetchItems();
    }
  }, []);

  return {
    loading,
    error,
    items,
    fetchItems,
    resetItems,
  };
};

export const useGetInventoryItem = (props: IGetInventoryItem) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [item, setItem] = useState<IInventory>({} as IInventory);

  const logger = getClientLogger('hooks.inventory.useGetInventoryItem');

  const fetchItem = async (fetchProps?: IGetInventoryItem): Promise<IInventory> => {
    const _props = fetchProps ?? props;

    try {
      setLoading(true);
      setError(false);

      const response = await fetch('/api/inventory/getItem', {
        method: 'POST',
        body: JSON.stringify(_props),
      });
      const data: IInventory = (await response.json()).data;

      setItem(data);

      setLoading(false);

      return data;
    } catch (error) {
      logger.error(`Error fetching inventory item: ${(error as Error).message}`);

      setLoading(false);
      setError(true);

      return {} as IInventory;
    }
  };

  useEffect(() => {
    if (!props?.skipFetch) {
      fetchItem();
    }
  }, []);

  return {
    loading,
    error,
    item,
    fetchItem,
  };
};
